import React, { useEffect } from "react";
import { Head, Link, useForm, usePage } from "@inertiajs/react";
import {
  Container,
  Title,
  Text,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Box,
  Paper,
  Grid,
  rem,
  Alert,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconAlertCircle,
  IconCheck,
  IconPlus,
} from "@tabler/icons-react";
import AppShell from "../../../Components/Layout/AppShell";

interface Props {
  project_types: string[];
  errors?: string[];
}

const AdminProjectsNew: React.FC<Props> = ({ project_types, errors }) => {
  const { props } = usePage();
  const { data, setData, post, processing } = useForm({
    title: "",
    description: "",
    client_name: "",
    project_url: "",
    project_type: "",
    project_subtype: "",
    status: "draft",
    featured_image: "",
    start_date: null as string | null,
    end_date: null as string | null,
    skills: "",
    tools: "",
  });

  // Show success notification when there's a flash message
  useEffect(() => {
    const notice = props.notice as string | undefined;
    if (notice) {
      notifications.show({
        title: "Success!",
        message: notice,
        color: "green",
        icon: <IconCheck size={16} />,
        autoClose: 4000,
        styles: {
          root: {
            backgroundColor: "rgba(34, 197, 94, 0.1)",
            border: "1px solid rgba(34, 197, 94, 0.2)",
          },
          title: { color: "rgba(34, 197, 94, 0.9)" },
          description: { color: "rgba(34, 197, 94, 0.8)" },
          icon: { backgroundColor: "rgba(34, 197, 94, 0.2)" },
        },
      });
    }
  }, [props.notice]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/admin/projects");
  };

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
  ];

  // Get subtype options based on selected project type
  const getSubtypeOptions = (projectType: string) => {
    switch (projectType) {
      case "Software Development":
        return [
          "Web Development",
          "Mobile Development",
          "Software Development",
          "DevOps/Infrastructure",
        ];
      case "Digital Media":
        return [
          "Photography",
          "Video Production",
          "Graphic Design",
          "Audio Production",
        ];
      case "Marketing & Strategy":
        return [
          "Digital Marketing",
          "Content Strategy",
          "SEO/Analytics",
          "Campaign Management",
        ];
      case "Teaching & Events":
        return [
          "Workshop Development",
          "Event Planning",
          "Dance Instruction",
          "Public Speaking",
        ];
      case "Business Consulting":
        return [
          "Process Optimization",
          "Strategic Planning",
          "Project Management",
          "Operations Consulting",
        ];
      default:
        return [];
    }
  };

  const subtypeOptions = getSubtypeOptions(data.project_type);

  // Clear subtype when project type changes
  const handleProjectTypeChange = (value: string | null) => {
    setData("project_type", value || "");
    setData("project_subtype", ""); // Clear subtype when main type changes
  };

  return (
    <AppShell>
      <Head title="New Project - Admin">
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <Box
        style={{
          background:
            "linear-gradient(180deg, #0a0a0b 0%, #111113 50%, #0a0a0b 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="lg" py="xl">
          <Stack gap="xl">
            {/* Header */}
            <Paper
              shadow="xl"
              radius="xl"
              p="xl"
              style={{
                background: "rgba(26, 27, 30, 0.95)",
                border: "1px solid rgba(55, 58, 64, 0.4)",
              }}
            >
              <Group justify="space-between" align="center">
                <Stack gap="xs">
                  <Group gap="md">
                    <Button
                      variant="subtle"
                      color="gray"
                      leftSection={<IconArrowLeft size={16} />}
                      component={Link}
                      href="/admin/projects"
                      style={{ color: "rgba(161, 161, 170, 0.8)" }}
                    >
                      Back to Projects
                    </Button>
                  </Group>
                  <Title
                    order={1}
                    style={{
                      color: "white",
                      fontFamily:
                        'Montserrat, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    Create New Project
                  </Title>
                  <Text size="lg" style={{ color: "rgba(161, 161, 170, 0.9)" }}>
                    Add a new project to your portfolio
                  </Text>
                </Stack>
              </Group>
            </Paper>

            {/* Error Messages */}
            {errors && errors.length > 0 && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Please fix the following errors:"
                color="red"
                style={{
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  color: "rgba(239, 68, 68, 0.9)",
                }}
              >
                <ul style={{ margin: 0, paddingLeft: rem(20) }}>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* New Project Form */}
            <Paper
              shadow="xl"
              radius="xl"
              p="xl"
              style={{
                background: "rgba(26, 27, 30, 0.95)",
                border: "1px solid rgba(55, 58, 64, 0.4)",
              }}
            >
              <form onSubmit={handleSubmit}>
                <Stack gap="lg">
                  {/* Basic Info */}
                  <Grid>
                    <Grid.Col span={12}>
                      <TextInput
                        label="Project Title"
                        placeholder="Enter project title"
                        value={data.title}
                        onChange={(e) => setData("title", e.target.value)}
                        required
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Project Type"
                        placeholder="Select project type"
                        value={data.project_type}
                        onChange={handleProjectTypeChange}
                        data={project_types}
                        required
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                          dropdown: {
                            backgroundColor: "rgba(26, 27, 30, 0.95)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                          },
                          option: {
                            color: "white",
                            "&[data-selected]": {
                              backgroundColor: "#f97316",
                            },
                            "&[data-hovered]": {
                              backgroundColor: "rgba(249, 115, 22, 0.1)",
                            },
                          },
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Project Subtype"
                        placeholder="Select subtype (optional)"
                        value={data.project_subtype}
                        onChange={(value) =>
                          setData("project_subtype", value || "")
                        }
                        data={subtypeOptions}
                        disabled={
                          !data.project_type || subtypeOptions.length === 0
                        }
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                            "&:disabled": {
                              backgroundColor: "rgba(20, 21, 23, 0.4)",
                              color: "rgba(161, 161, 170, 0.5)",
                            },
                          },
                          dropdown: {
                            backgroundColor: "rgba(26, 27, 30, 0.95)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                          },
                          option: {
                            color: "white",
                            "&[data-selected]": {
                              backgroundColor: "#f97316",
                            },
                            "&[data-hovered]": {
                              backgroundColor: "rgba(249, 115, 22, 0.1)",
                            },
                          },
                        }}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Status"
                        placeholder="Select status"
                        value={data.status}
                        onChange={(value) => setData("status", value || "")}
                        data={statusOptions}
                        required
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                          dropdown: {
                            backgroundColor: "rgba(26, 27, 30, 0.95)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                          },
                          option: {
                            color: "white",
                            "&[data-selected]": {
                              backgroundColor: "#f97316",
                            },
                            "&[data-hovered]": {
                              backgroundColor: "rgba(249, 115, 22, 0.1)",
                            },
                          },
                        }}
                      />
                    </Grid.Col>
                  </Grid>

                  {/* Description */}
                  <Textarea
                    label="Description"
                    placeholder="Enter project description"
                    value={data.description}
                    onChange={(e) => setData("description", e.target.value)}
                    rows={4}
                    styles={{
                      label: {
                        color: "white",
                        fontWeight: 500,
                        marginBottom: rem(8),
                      },
                      input: {
                        backgroundColor: "rgba(20, 21, 23, 0.8)",
                        border: "1px solid rgba(55, 58, 64, 0.4)",
                        color: "white",
                        "&:focus": {
                          borderColor: "#f97316",
                        },
                      },
                    }}
                  />

                  {/* Client and URLs */}
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Client Name"
                        placeholder="Enter client name (optional)"
                        value={data.client_name}
                        onChange={(e) => setData("client_name", e.target.value)}
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Project URL"
                        placeholder="https://example.com or YouTube URL"
                        value={data.project_url}
                        onChange={(e) => setData("project_url", e.target.value)}
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>
                  </Grid>

                  {/* Featured Image */}
                  <TextInput
                    label="Featured Image URL"
                    placeholder="https://example.com/image.jpg"
                    value={data.featured_image}
                    onChange={(e) => setData("featured_image", e.target.value)}
                    styles={{
                      label: {
                        color: "white",
                        fontWeight: 500,
                        marginBottom: rem(8),
                      },
                      input: {
                        backgroundColor: "rgba(20, 21, 23, 0.8)",
                        border: "1px solid rgba(55, 58, 64, 0.4)",
                        color: "white",
                        "&:focus": {
                          borderColor: "#f97316",
                        },
                      },
                    }}
                  />

                  {/* Dates */}
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <DateInput
                        label="Start Date"
                        placeholder="Select start date"
                        value={data.start_date}
                        onChange={(value) => setData("start_date", value)}
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <DateInput
                        label="End Date"
                        placeholder="Select end date"
                        value={data.end_date}
                        onChange={(value) => setData("end_date", value)}
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>
                  </Grid>

                  {/* Skills and Tools */}
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Skills"
                        placeholder="Motion Graphics, DJing, Visual Design"
                        value={data.skills}
                        onChange={(e) => setData("skills", e.target.value)}
                        description="Separate multiple skills with commas"
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          description: {
                            color: "rgba(161, 161, 170, 0.6)",
                            fontSize: rem(12),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Tools"
                        placeholder="After Effects, DJ Equipment, Adobe Suite"
                        value={data.tools}
                        onChange={(e) => setData("tools", e.target.value)}
                        description="Separate multiple tools with commas"
                        styles={{
                          label: {
                            color: "white",
                            fontWeight: 500,
                            marginBottom: rem(8),
                          },
                          description: {
                            color: "rgba(161, 161, 170, 0.6)",
                            fontSize: rem(12),
                          },
                          input: {
                            backgroundColor: "rgba(20, 21, 23, 0.8)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                            },
                          },
                        }}
                      />
                    </Grid.Col>
                  </Grid>

                  {/* Form Actions */}
                  <Group justify="flex-end" gap="md" pt="lg">
                    <Button
                      variant="outline"
                      color="gray"
                      component={Link}
                      href="/admin/projects"
                      style={{
                        borderColor: "rgba(55, 58, 64, 0.4)",
                        color: "rgba(161, 161, 170, 0.9)",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="gradient"
                      gradient={{ from: "orange.6", to: "orange.7", deg: 135 }}
                      leftSection={<IconPlus size={16} />}
                      loading={processing}
                      style={{ fontWeight: 600 }}
                    >
                      Create Project
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </AppShell>
  );
};

export default AdminProjectsNew;
